<template>
    <div class="relative">
        <label>
            <input
                class="h-10 w-full flex items-center gap-2 text-left pl-3 text-slate-500 relative bg-white rounded border border-slate-200 py-1.5 placeholder:text-slate-500 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                v-model="programQuery"
                @focus="() => (showList = true)"
                ref="transferProgramsButton"
                v-click-outside="() => (showList = false)"
                :class="tripFormErrors?.transfer_program_id ? '!border-red-500' : ''"
            />
            <span class="w-[90%] flex items-center text-sm gap-2 pr-14 absolute left-2 top-1/2 -translate-y-1/2 whitespace-nowrap truncate">
                <ImageLoader v-if="programChosen">
                    <template #image>
                        <img
                            :src="`${cdnUrl}/${programChosen?.program_image?.path}/_small/${programChosen?.program_image?.file_name}`"
                            :alt="programChosen?.name"
                            class="max-w-10 max-h-5 rounded-sm"
                        />
                    </template>
                    <template #error>
                        <Icon icon="points" class="text-slate-600 w-8 h-5" />
                    </template>
                </ImageLoader>
                {{ programChosen?.name }}
            </span>
            <button
                class="block leading-none text-red-600 w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                v-if="programChosen"
                @click="clearProgram()"
            >
                <Icon icon="close" />
            </button>
        </label>
        <div
            class="text-red-500 text-xs flex items-center gap-2 py-1.5"
            role="alert"
            v-if="tripFormErrors?.transfer_program_id"
        >
            <Icon icon="caution" />
            Please choose a transfer points currency
        </div>
        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 p-2 overflow-y-auto bg-white rounded-lg shadow-sm border border-slate-200 absolute z-[99999]"
                    v-if="showList"
                    :style="`width: ${width}px; top: ${top}px; left: ${left}px`"
                >
                    <label
                        class="w-full flex items-center gap-2 px-3 py-2 text-slate-800 text-sm bg-white rounded hover:bg-slate-100 transition-colors mb-1 cursor-pointer"
                        v-for="program in transferProgramsFiltered"
                        :key="program.id"
                        @click="selectProgram(program)"
                    >
                        <input
                            class="sr-only"
                            type="radio"
                            name="transfer_program_id"
                            :value="program.id"
                            :checked="programChosen?.id === program.id"
                        />
                        <div class="w-12 flex justify-center">
                            <ImageLoader>
                                <template #image>
                                    <img
                                        :src="`${cdnUrl}/${program.image?.path}/_small/${program.image?.file_name}`"
                                        :alt="program?.label"
                                        class="max-w-10 max-h-5 rounded-sm"
                                    />
                                </template>
                                <template #error>
                                    <Icon icon="points" class="text-slate-600 w-8 h-5" />
                                </template>
                            </ImageLoader>
                        </div>
                        {{ program?.label }}
                    </label>
                </div>
            </transition>
        </Teleport>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import {
    onMounted,
    onBeforeUnmount,
    ref,
    useTemplateRef,
    watch,
    computed,
} from 'vue';
import { useTripsStore } from '@/store/trips';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

const props = defineProps({
    currentProgramId: {
        type: Number,
        default: null
    },
    tripFormErrors: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update:currentProgramId']);

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const tripsStore = useTripsStore();
const { programs } = storeToRefs(tripsStore);

const programQuery = ref(null);
const showList = ref(false);
const programsButton = useTemplateRef('transferProgramsButton');
const top = ref(0);
const left = ref(0);
const width = ref(80);

const programChosen = computed(() => {
    if (!props.currentProgramId) return null;
    return programs.value.find(p => p.id === props.currentProgramId);
});

const selectProgram = (program) => {
    emit('update:currentProgramId', program.id);
    showList.value = false;
    programQuery.value = null;
};

const clearProgram = () => {
    emit('update:currentProgramId', null);
};

const setProgramsDropdownPosition = () => {
    const position = programsButton.value.getBoundingClientRect();
    top.value = position.top + 42;
    left.value = position.left;
    width.value = position.width;
};

watch(showList, (val) => {
    if (val) {
        setProgramsDropdownPosition();
    }
});

const transferProgramsFiltered = computed(() => {
    const prefilteredPrograms = programs.value
        .map((program) => {
            return {
                label: program.name,
                value: program.type,
                id: program.id,
                image: program.program_image,
            };
        })
        .filter((program) => program.value === 'flexible_currency');

    return prefilteredPrograms.filter((program) => {
        return programQuery.value && programQuery.value.length > 0
            ? program.label
                  .toLowerCase()
                  .replace('.', '')
                  .includes(programQuery.value.toLowerCase())
            : true;
    });
});

onMounted(() => {
    window.addEventListener('resize', setProgramsDropdownPosition);
    document
        .querySelector('.ui-modal-content')
        .addEventListener('scroll', setProgramsDropdownPosition, true);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', setProgramsDropdownPosition);
    document
        .querySelector('.ui-modal-content')
        .removeEventListener('scroll', setProgramsDropdownPosition, true);
});
</script>
